import { useState, useEffect, useRef } from '@wordpress/element';
import { useDispatch }                  from '@wordpress/data';
import { __ }                           from '@wordpress/i18n';
import { STORE_NAME }                   from '../../../store';
import { fetchPriceRange }              from '../../../utils/api';
import { debounce }                     from '../../../utils/helpers';

export default function PriceFilter( { type = 'range', showLabel = true } ) {
	const [ absoluteMax, setAbsoluteMax ] = useState( 1000 );
	const [ min, setMin ] = useState( 0 );
	const [ max, setMax ] = useState( 1000 );

	const { setMinPrice, setMaxPrice } = useDispatch( STORE_NAME );

	const debouncedMin = useRef( debounce( ( v ) => setMinPrice( v > 0 ? v : '' ), 400 ) );
	const debouncedMax = useRef( debounce( ( v, absMax ) => setMaxPrice( v < absMax ? v : '' ), 400 ) );

	useEffect( () => {
		fetchPriceRange().then( ( data ) => {
			const absMax = Math.ceil( data.max );
			setAbsoluteMax( absMax );
			setMax( absMax );
		} ).catch( () => {} );
	}, [] );

	function handleMinChange( raw ) {
		const v = Math.max( 0, Math.min( Number( raw ) || 0, max - 1 ) );
		setMin( v );
		debouncedMin.current( v );
	}

	function handleMaxChange( raw ) {
		const v = Math.max( min + 1, Math.min( Number( raw ) || absoluteMax, absoluteMax ) );
		setMax( v );
		debouncedMax.current( v, absoluteMax );
	}

	const minPercent = absoluteMax > 0 ? ( min / absoluteMax ) * 100 : 0;
	const maxPercent = absoluteMax > 0 ? ( max / absoluteMax ) * 100 : 100;
	const currency   = window.mwsbData?.currency || '$';

	return (
		<div className="mwsb-filter mwsb-filter--price">
			<div className="mwsb-filter__title-row">
				{ showLabel && <h4 className="mwsb-filter__title">{ __( 'Price', 'mwsb' ) }</h4> }
				<span className="mwsb-price-range-display">
					{ currency }{ min } &mdash; { currency }{ max }
				</span>
			</div>

			{ type === 'range' && (
				<div className="mwsb-price-slider">
					<div className="mwsb-price-slider__track">
						<div
							className="mwsb-price-slider__fill"
							style={ { left: `${ minPercent }%`, width: `${ maxPercent - minPercent }%` } }
						/>
					</div>
					<input
						type="range"
						className="mwsb-price-slider__input mwsb-price-slider__input--min"
						min={ 0 }
						max={ absoluteMax }
						value={ min }
						onChange={ ( e ) => handleMinChange( e.target.value ) }
					/>
					<input
						type="range"
						className="mwsb-price-slider__input mwsb-price-slider__input--max"
						min={ 0 }
						max={ absoluteMax }
						value={ max }
						onChange={ ( e ) => handleMaxChange( e.target.value ) }
					/>
				</div>
			) }

			<div className="mwsb-price-inputs">
				<div className="mwsb-price-input-wrap">
					<span className="mwsb-price-currency">{ currency }</span>
					<input
						type="number"
						className="mwsb-price-input"
						placeholder="0"
						min={ 0 }
						max={ max - 1 }
						value={ min === 0 ? '' : min }
						onChange={ ( e ) => handleMinChange( e.target.value ) }
						aria-label={ __( 'Minimum price', 'mwsb' ) }
					/>
				</div>
				<span className="mwsb-price-separator" />
				<div className="mwsb-price-input-wrap">
					<span className="mwsb-price-currency">{ currency }</span>
					<input
						type="number"
						className="mwsb-price-input"
						placeholder={ String( absoluteMax ) }
						min={ min + 1 }
						max={ absoluteMax }
						value={ max === absoluteMax ? '' : max }
						onChange={ ( e ) => handleMaxChange( e.target.value ) }
						aria-label={ __( 'Maximum price', 'mwsb' ) }
					/>
				</div>
			</div>
		</div>
	);
}
